/**
 * Pattern unlocks (M8 plan MC-f3, decision 12): every route writes the one store, `career.patterns.unlocked`, and a
 * rung there stays unlocked. `features/craftStudy/feature.ts` registers study and the route recorder on the hook.
 *
 * Routes (each pattern's `patterns.json routes`):
 * - **Standing:** standing with the pattern's `standingFaction` at `standingReq.mk2` (25) opens Mk II and at
 *   `standingReq.mk3` (40) Mk III. `holdfast_old` has none. A rung opens the moment standing meets it
 *   (`isUnlocked`), and `recordRouteUnlocks` writes it into the store (the feature runs it whenever the career
 *   changes), so standing that falls later, a war included, never takes it back.
 * - **Study:** at any outfitting station, consume `patternStudy.json partsPerStudy` (3) Intact (≥ `salvage.json
 *   bands.intact`, D-362) hold components made by the pattern's maker (D-498); Mk III takes Mk III ones. Standing
 *   is never read, so a war with the maker never closes study. Serviceable parts are refused (SY-5b recondition
 *   brings them up to Intact, D-374), and so are sealed, story and contract parts and pods with contents. A
 *   `first_charter` pattern (`holdfast_old`) takes only Rush-pattern parts (`provenance: 'rush'`, D-502), since
 *   every derelict part is `first_charter`. There is no "buy components" fallback: no component purchase path exists.
 * - **Fragments:** MC-h5a's `career.blueprints.fragments` counter (MC-0a's `blueprints?` key): every
 *   `blueprints.json fragmentsPerRung` fragments open the next rung of the ladder (`blueprints.json ladder`, or,
 *   while that is empty, Mk II then Mk III of each pattern with a fragments route: `holdfast_old`). MC-h5a writes
 *   the store as it decodes; this module also reads the counter, so either order of landing is consistent.
 */
import { z } from 'zod'
import { fail, ok, type CmdResult } from '../career/result'
import type { CargoComponent, Career, PatternId, PatternUnlockId } from '../career/types'
import { PATTERN_IDS } from '../career/types'
import type { M2Data, StationDef } from '../data/m2Schemas'
import { loadPatterns, type PatternDef, type PatternsFile } from '../data/patterns'
import patternStudyJson from '../data/patternStudy.json'
import { CONDITION_BAND, conditionBand } from '../salvage/generate'
import { loadBlueprints, type BlueprintsFile } from './blueprints'

/** A pattern rung: patterns cover Mk II and Mk III only. */
export type PatternRung = 'mk2' | 'mk3'
export const PATTERN_RUNGS = ['mk2', 'mk3'] as const satisfies readonly PatternRung[]

declare module './hook' {
  interface CraftCommandMap {
    /**
     * Studies `pattern` at the docked (outfitting) station: `rung`, or by default the first rung not yet unlocked
     * (Mk II, then Mk III).
     */
    'pattern.study': { kind: 'pattern.study'; pattern: PatternId; rung?: PatternRung }
    /** Writes the rungs standing and fragments open now into the store (`recordRouteUnlocks`); a no-op when none is new. */
    'pattern.record': { kind: 'pattern.record' }
  }
}

export const patternStudyFileSchema = z.strictObject({
  /** Intact maker components one study consumes (decision 12). */
  partsPerStudy: z.number().int().positive(),
})
export type PatternStudyFile = z.infer<typeof patternStudyFileSchema>

/** Parses `patternStudy.json`; throws naming the bad path. */
export function loadPatternStudy(raw: unknown = patternStudyJson): PatternStudyFile {
  return patternStudyFileSchema.parse(raw)
}

/** The data the unlock routes read (tests pass their own). */
export interface UnlockData {
  patterns: PatternsFile
  blueprints: Pick<BlueprintsFile, 'fragmentsPerRung' | 'ladder'>
  study: PatternStudyFile
}

let shipped: UnlockData | null = null

/** The shipped `patterns.json`, `blueprints.json` and `patternStudy.json` (parsed once). */
export function unlockData(): UnlockData {
  shipped ??= { patterns: loadPatterns(), blueprints: loadBlueprints(), study: loadPatternStudy() }
  return shipped
}

export const UNLOCK_TEXTS = {
  noService: '{station} has no outfitting service.',
  noPattern: 'There is no such pattern.',
  noStudy: 'The {pattern} pattern cannot be studied.',
  already: 'The {pattern} Mk {mk} pattern is already unlocked.',
  short: 'Studying the {pattern} Mk {mk} pattern takes {need} Intact {parts} in the hold; you have {have}.',
  shortServiceable: ' Recondition Serviceable parts to Intact first.',
  partsMaker: 'parts of its maker',
  partsMk3: 'Mk III parts of its maker',
  partsRush: 'Rush-pattern parts',
} as const

/** The store id of `pattern`'s `rung`. */
export function unlockId(pattern: PatternId, rung: PatternRung): PatternUnlockId {
  return `${pattern}.${rung}`
}

/** The rungs written in the store. */
export function storedUnlocks(career: Career): readonly PatternUnlockId[] {
  return career.patterns?.unlocked ?? []
}

/** The rungs the standing route opens now (standing at each pattern's `standingFaction` ≥ `standingReq`). */
export function standingUnlocks(career: Career, data: UnlockData = unlockData()): PatternUnlockId[] {
  const out: PatternUnlockId[] = []
  const req = data.patterns.standingReq
  for (const id of PATTERN_IDS) {
    const p = data.patterns.patterns[id]
    if (p === undefined || p.standingFaction === null || !p.routes.includes('standing')) continue
    const standing = career.standings[p.standingFaction]
    for (const rung of PATTERN_RUNGS) if (standing >= req[rung]) out.push(unlockId(id, rung))
  }
  return out
}

/** The fragment ladder: `blueprints.json ladder`, or Mk II then Mk III of each pattern with a fragments route. */
export function fragmentLadder(data: UnlockData = unlockData()): readonly PatternUnlockId[] {
  if (data.blueprints.ladder.length > 0) return data.blueprints.ladder
  const out: PatternUnlockId[] = []
  for (const id of PATTERN_IDS) {
    if (data.patterns.patterns[id]?.routes.includes('fragments') !== true) continue
    for (const rung of PATTERN_RUNGS) out.push(unlockId(id, rung))
  }
  return out
}

/** The rungs the fragment counter opens now: rung k of the ladder at `(k + 1) × fragmentsPerRung` fragments. */
export function fragmentUnlocks(career: Career, data: UnlockData = unlockData()): PatternUnlockId[] {
  const fragments = career.blueprints?.fragments ?? 0
  const rungs = Math.floor(fragments / data.blueprints.fragmentsPerRung)
  return fragmentLadder(data).slice(0, Math.max(0, rungs))
}

/** Whether `id` is unlocked: in the store, or opened now by standing or fragments. */
export function isUnlocked(career: Career, id: PatternUnlockId, data: UnlockData = unlockData()): boolean {
  return storedUnlocks(career).includes(id) || standingUnlocks(career, data).includes(id) || fragmentUnlocks(career, data).includes(id)
}

/** `career` with `ids` added to the store (after the stored ones, in `ids` order); the same Career when none is new. */
export function withUnlocks(career: Career, ids: readonly PatternUnlockId[]): Career {
  const stored = storedUnlocks(career)
  const fresh = ids.filter((id, i) => !stored.includes(id) && ids.indexOf(id) === i)
  if (fresh.length === 0) return career
  return { ...career, patterns: { unlocked: [...stored, ...fresh] } }
}

/** Writes every rung standing or fragments open now into the store; the same Career when none is new. */
export function recordRouteUnlocks(career: Career, data: UnlockData = unlockData()): Career {
  return withUnlocks(career, [...standingUnlocks(career, data), ...fragmentUnlocks(career, data)])
}

/** Whether studying `def` takes Rush-pattern parts (a `first_charter` pattern: every derelict part is first_charter). */
function needsRush(def: PatternDef): boolean {
  return def.maker === 'first_charter'
}

/** A part study may consume for `def`'s `rung`, before its condition: the maker's, not held for anything, right Mk. */
function studyKind(c: CargoComponent, def: PatternDef, rung: PatternRung): boolean {
  if (c.sealed === true || c.story === true || c.contractId !== undefined || c.contents !== undefined) return false
  if (c.maker !== def.maker) return false
  if (needsRush(def) && c.provenance !== 'rush') return false
  return rung !== 'mk3' || c.mk === 3
}

/**
 * The hold components a study of `pattern`'s `rung` may consume, cheapest first (ties in hold order): Intact parts of
 * the pattern's maker (Mk III for Mk III; Rush-pattern for a `first_charter` pattern), never sealed, story, contract
 * or a pod with contents.
 */
export function studyParts(
  career: Career,
  data: Pick<M2Data, 'salvage'>,
  pattern: PatternId,
  rung: PatternRung,
  unlocks: UnlockData = unlockData(),
): CargoComponent[] {
  const def = unlocks.patterns.patterns[pattern]
  if (def === undefined) return []
  const bands = data.salvage.bands
  return career.cargo.components
    .filter((c) => studyKind(c, def, rung) && conditionBand(c.condition, bands) === CONDITION_BAND.intact)
    .sort((a, b) => a.value - b.value)
}

/** Serviceable hold parts that would count once reconditioned (the refusal's hint). */
function serviceableParts(career: Career, data: Pick<M2Data, 'salvage'>, def: PatternDef, rung: PatternRung): number {
  let n = 0
  for (const c of career.cargo.components) {
    if (studyKind(c, def, rung) && conditionBand(c.condition, data.salvage.bands) === CONDITION_BAND.serviceable) n++
  }
  return n
}

/** The first rung of `pattern` not yet unlocked by any route (Mk II, then Mk III), or null when both are. */
export function nextStudyRung(career: Career, pattern: PatternId, data: UnlockData = unlockData()): PatternRung | null {
  for (const rung of PATTERN_RUNGS) if (!isUnlocked(career, unlockId(pattern, rung), data)) return rung
  return null
}

function mkLabel(rung: PatternRung): string {
  return rung === 'mk2' ? 'II' : 'III'
}

/**
 * Studies `pattern`'s `rung` at an outfitting station: consumes `partsPerStudy` Intact hold components of its maker
 * (`studyParts`, cheapest first) and writes the rung into the store. Refuses (Career untouched) at a station without
 * outfitting (`serviceUnavailable`), for a pattern with no study route (`serviceUnavailable`), a rung already unlocked
 * by any route (`invalidAmount`, `detail.reason: 'alreadyUnlocked'`), or too few parts (`notInCargo`, with `have`, `need`
 * and `serviceable` in `detail`). Standing only counts toward "already unlocked": it never closes study, and the
 * station's owner doesn't matter.
 */
export function study(
  career: Career,
  data: Pick<M2Data, 'salvage'>,
  station: StationDef,
  pattern: PatternId,
  rung: PatternRung,
  unlocks: UnlockData = unlockData(),
): CmdResult<Career> {
  if (!station.services.includes('outfitting')) {
    return fail('serviceUnavailable', UNLOCK_TEXTS.noService.replace('{station}', station.name), { station: station.id })
  }
  const def = unlocks.patterns.patterns[pattern]
  if (def === undefined) return fail('serviceUnavailable', UNLOCK_TEXTS.noPattern, { pattern })
  if (!def.routes.includes('study')) {
    return fail('serviceUnavailable', UNLOCK_TEXTS.noStudy.replace('{pattern}', def.name), { pattern })
  }
  const id = unlockId(pattern, rung)
  if (isUnlocked(career, id, unlocks)) {
    const message = UNLOCK_TEXTS.already.replace('{pattern}', def.name).replace('{mk}', mkLabel(rung))
    return fail('invalidAmount', message, { pattern, rung, reason: 'alreadyUnlocked' })
  }
  const need = unlocks.study.partsPerStudy
  const parts = studyParts(career, data, pattern, rung, unlocks)
  if (parts.length < need) {
    const serviceable = serviceableParts(career, data, def, rung)
    const what = needsRush(def) ? UNLOCK_TEXTS.partsRush : rung === 'mk3' ? UNLOCK_TEXTS.partsMk3 : UNLOCK_TEXTS.partsMaker
    const message =
      UNLOCK_TEXTS.short
        .replace('{pattern}', def.name)
        .replace('{mk}', mkLabel(rung))
        .replace('{need}', String(need))
        .replace('{parts}', what)
        .replace('{have}', String(parts.length)) + (serviceable > 0 ? UNLOCK_TEXTS.shortServiceable : '')
    return fail('notInCargo', message, { pattern, rung, have: parts.length, need, serviceable })
  }
  const used = new Set(parts.slice(0, need).map((c) => c.id))
  const components = career.cargo.components.filter((c) => !used.has(c.id))
  return ok(withUnlocks({ ...career, cargo: { ...career.cargo, components } }, [id]))
}
